import { appRoutes } from '../../shared/routes/app-routes';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { HasPermission } from '../../shared/directives/has-permission/has-permission.service';
import { ROW_ACTIONS, RowActionDefinition } from '../../shared/design/row-actions';
import { CurrentUserService } from '../../shared/services/current-user.service';
import { normalizePermission } from '../menu-permission';
import { GlobalSearchGroupKey, GlobalSearchResponse, GlobalSearchService } from './global-search.service';
import { ScreenEntry, buildScreenCatalog, popularScreens, searchScreens } from './screen-search';

interface SearchResultAction {
  definition: RowActionDefinition;
  label: string;
  route: any[];
}

interface SearchResultItem {
  id: string | number;
  title: string;
  subtitle: string;
  route: any[];
  queryParams?: any;
  icon?: string;
  actions?: SearchResultAction[];
  editRoute?: any[];
}

interface SearchResultGroup {
  key: GlobalSearchGroupKey | 'screens';
  label: string;
  icon: string;
  total: number;
  items: SearchResultItem[];
  seeAllRoute: any[];
  seeAllQueryParams?: any;
}

type SearchState = 'idle' | 'loading' | 'done' | 'error';

const MIN_CHARS = 2;
const GROUP_ORDER: GlobalSearchGroupKey[] = ['events', 'products', 'clients'];

@Component({
  selector: 'ss-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
  standalone: false
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  isOpen = false;
  term = '';
  state: SearchState = 'idle';
  groups: SearchResultGroup[] = [];
  flatItems: SearchResultItem[] = [];
  activeIndex = -1;
  shortcutLabel = '⌘K';
  editShortcutLabel = '⌘ Enter';

  private terms$ = new Subject<string>();
  private subscription?: Subscription;
  private userSubscription?: Subscription;
  private screenCatalog: ScreenEntry[] = [];
  private apiGroups: SearchResultGroup[] = [];
  private screenGroup: SearchResultGroup | null = null;
  private screenRank = 3;

  constructor(private globalSearchService: GlobalSearchService, private router: Router, private currentUserService: CurrentUserService) { }

  ngOnInit() {
    const platform = (navigator as any).userAgentData?.platform || navigator.platform || '';
    const isApple = /mac|iphone|ipad/i.test(platform);
    this.shortcutLabel = isApple ? '⌘K' : 'Ctrl K';
    this.editShortcutLabel = isApple ? '⌘ Enter' : 'Ctrl Enter';

    this.subscription = this.terms$.pipe(
      map((term) => term.trim()),
      debounceTime(300),
      distinctUntilChanged(),
      tap((term) => {
        this.activeIndex = -1;
        this.state = term.length >= MIN_CHARS ? 'loading' : 'idle';
        if (term.length < MIN_CHARS) {
          this.apiGroups = [];
          this.compose();
        }
      }),
      switchMap((term) => {
        if (term.length < MIN_CHARS) {
          return of(null);
        }
        return this.globalSearchService.search(term).pipe(
          catchError(() => {
            this.state = 'error';
            return of(null);
          })
        );
      })
    ).subscribe((response) => {
      if (!response) {
        return;
      }
      this.apiGroups = this.buildGroups(response);
      this.compose();
      this.state = 'done';
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.userSubscription?.unsubscribe();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.isOpen ? this.focusInput() : this.open();
    } else if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  open() {
    this.isOpen = true;
    this.loadScreens();
    setTimeout(() => this.focusInput());
  }

  close() {
    this.isOpen = false;
    this.activeIndex = -1;
  }

  onInput(value: string) {
    this.term = value;
    this.activeIndex = -1;
    this.refreshScreens();
    this.terms$.next(value);
  }

  retry() {
    const term = this.term.trim();
    if (term.length < MIN_CHARS) {
      return;
    }
    this.state = 'loading';
    this.globalSearchService.search(term).subscribe({
      next: (response) => {
        this.apiGroups = this.buildGroups(response);
        this.compose();
        this.state = 'done';
      },
      error: () => this.state = 'error'
    });
  }

  onInputKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.flatItems.length) {
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + step + this.flatItems.length) % this.flatItems.length;
      this.scrollActiveIntoView();
    } else if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault();
      const item = this.flatItems[this.activeIndex];
      if ((event.ctrlKey || event.metaKey) && item.editRoute) {
        this.openAction(item.editRoute);
      } else {
        this.select(item);
      }
    }
  }

  indexOf(item: SearchResultItem): number {
    return this.flatItems.indexOf(item);
  }

  select(item: SearchResultItem) {
    this.router.navigate(item.route, { queryParams: item.queryParams });
    this.reset();
  }

  openAction(route: any[]) {
    this.router.navigate(route);
    this.reset();
  }

  seeAll(group: SearchResultGroup) {
    this.router.navigate(group.seeAllRoute, { queryParams: group.seeAllQueryParams });
    this.reset();
  }

  get hasMinChars(): boolean {
    return this.term.trim().length >= MIN_CHARS;
  }

  private reset() {
    this.close();
    this.term = '';
    this.refreshScreens();
    this.terms$.next('');
  }

  private focusInput() {
    this.searchInput?.nativeElement.focus();
    this.searchInput?.nativeElement.select();
  }

  private scrollActiveIntoView() {
    setTimeout(() => {
      document.getElementById(`ss-global-search-option-${this.activeIndex}`)?.scrollIntoView({ block: 'nearest' });
    });
  }

  private loadScreens() {
    if (this.screenCatalog.length || this.userSubscription) {
      this.refreshScreens();
      return;
    }

    this.userSubscription = this.currentUserService.load().subscribe({
      next: (user) => {
        const menuPermissions = (user.menu_permissions || []).map((permission) => normalizePermission(permission));
        this.screenCatalog = buildScreenCatalog(menuPermissions, user.permissions || {});
        this.refreshScreens();
      },
      error: () => this.userSubscription = undefined
    });
  }

  private refreshScreens() {
    const term = this.term.trim();
    const matches = term ? searchScreens(term, this.screenCatalog) : popularScreens(this.screenCatalog).map((entry) => ({ entry, rank: 3 }));
    this.screenRank = matches.length ? Math.min(...matches.map((match) => match.rank)) : 3;
    this.screenGroup = matches.length ? {
      key: 'screens',
      label: term ? 'Telas' : 'Atalhos',
      icon: 'layout-grid',
      total: matches.length,
      seeAllRoute: [],
      items: matches.map(({ entry }) => ({
        id: entry.route.join('/'),
        title: entry.title,
        subtitle: entry.module,
        route: entry.route,
        icon: entry.icon
      }))
    } : null;
    this.compose();
  }

  /** Telas cujo nome começa com o termo vêm antes dos resultados da API; as demais, depois. */
  private compose() {
    const screens = this.screenGroup ? [this.screenGroup] : [];
    this.setGroups(this.screenRank <= 1 ? [...screens, ...this.apiGroups] : [...this.apiGroups, ...screens]);
  }

  private setGroups(groups: SearchResultGroup[]) {
    this.groups = groups;
    this.flatItems = groups.reduce((all, group) => all.concat(group.items), [] as SearchResultItem[]);
  }

  private buildGroups(response: GlobalSearchResponse): SearchResultGroup[] {
    const results = response?.results || {};

    return GROUP_ORDER
      .filter((key) => results[key] && results[key]!.items?.length)
      .map((key) => {
        const group = results[key]!;
        switch (key) {
          case 'events':
            return {
              key, label: 'Eventos', icon: 'calendar', total: group.total_entries,
              seeAllRoute: appRoutes.events.list(),
              seeAllQueryParams: { nome: this.term.trim() },
              items: group.items.map((event: any) => this.withActions({
                id: event.id,
                title: event.name,
                subtitle: [event.client_name, this.formatDate(event.start_date)].filter(Boolean).join(' · '),
                route: appRoutes.events.dashboard(event.id)
              }, { definition: ROW_ACTIONS.dashboard, label: 'Abrir dashboard do evento' }, 'event', 'Editar evento', appRoutes.events.edit(event.id)))
            };
          case 'products':
            return {
              key, label: 'Produtos', icon: 'package', total: group.total_entries,
              seeAllRoute: appRoutes.products.list(),
              seeAllQueryParams: { nome: this.term.trim() },
              items: group.items.map((product: any) => ({
                id: product.id,
                title: product.name,
                subtitle: [product.category_name, product.client_name].filter(Boolean).join(' · '),
                route: appRoutes.products.edit(product.id)
              }))
            };
          default:
            return {
              key, label: 'Clientes', icon: 'building-2', total: group.total_entries,
              seeAllRoute: appRoutes.clients.list(),
              seeAllQueryParams: { nome: this.term.trim() },
              items: group.items.map((client: any) => this.withActions({
                id: client.id,
                title: client.name,
                subtitle: [client.document, [client.city, client.state].filter(Boolean).join('/')].filter(Boolean).join(' · '),
                route: appRoutes.clients.details(client.id)
              }, { definition: ROW_ACTIONS.details, label: 'Ver detalhes do cliente' }, 'client', 'Editar cliente', appRoutes.clients.edit(client.id)))
            };
        }
      });
  }

  private withActions(item: SearchResultItem, view: Omit<SearchResultAction, 'route'>, entity: string, editLabel: string, editRoute: any[]): SearchResultItem {
    const actions: SearchResultAction[] = [{ ...view, route: item.route }];

    if (HasPermission.check(entity, 'update')) {
      actions.push({ definition: ROW_ACTIONS.edit, label: editLabel, route: editRoute });
      return { ...item, actions, editRoute };
    }

    return { ...item, actions };
  }

  private formatDate(value?: string): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString('pt-BR');
  }
}
